import { MIN_AMOUNT_NEW_MORTGAGE } from "../../components/screens/1-configuration/mortgage-configurator/MortageSlider";
import { Mortgage } from "../../model/MortgageExtensionModel";
import { round } from "../../utils/NumberUtils";

/**
 * Module for calculating various financial data.
 */
/**
 * Calculates monthly payment based on the provided mortgage data.
 * Mutates mortgage object.
 */

export default class FinanceCalculatorService {
    calcMonthlyPayment(mortgage: Mortgage) {
        mortgage.monthlyPayment = round(((mortgage.interestRate / 100) * mortgage.amount) / 12);
    }

    /**
     * Calculates the interest rate and relevant financial data based on the duration of the mortgage.
     * Mutates mortgage object.
     */

    onDurationChange(mortgage: Mortgage) {
        this.validateMortgage(mortgage);

        for (const offer of mortgage.offers) {
            if (offer.duration === Number(mortgage.duration)) {
                mortgage.interestRate = offer.value;
                mortgage.soko = offer.soko;
                break;
            }
        }
        this.calculateOtherParameters(mortgage);
    }

    /**
     * Triggered when amount has been changed.
     * Mutates mortgage object.
     */

    onAmountChange(value: number, mortgage: Mortgage, maxAmountOfNewMortgage: number) {
        this.validateMortgage(mortgage);

        if (value === mortgage.amount) {
            return;
        }
        // do not let slider go over max amount
        if (value > maxAmountOfNewMortgage) {
            value = maxAmountOfNewMortgage;
        }
        mortgage.amount = value;
        mortgage.repayment = maxAmountOfNewMortgage - mortgage.amount;

        this.calculateOtherParameters(mortgage);
    }

    /**
     * Triggered when repayment has been changed.
     * Mutates mortgage object.
     */

    onRepaymentChange(value: number, mortgage: Mortgage, maxAmountOfNewMortgage: number) {
        this.validateMortgage(mortgage);

        if (value === mortgage.repayment) {
            return;
        }
        mortgage.repayment = value;
        let newAmount = maxAmountOfNewMortgage - mortgage.repayment;

        // never allow amount to go below min defined value
        if (newAmount < MIN_AMOUNT_NEW_MORTGAGE) {
            newAmount = MIN_AMOUNT_NEW_MORTGAGE;
            // recalculate repayment in case this happens
            mortgage.repayment = maxAmountOfNewMortgage - newAmount;
        }
        mortgage.amount = newAmount;

        this.calculateOtherParameters(mortgage);
    }

    calculateOtherParameters(mortgage: Mortgage) {
        if (mortgage.interestRate) {
            this.calcMonthlyPayment(mortgage);
        }
        mortgage.totalSumLiabilities = mortgage.amount + mortgage.totalSumOtherLiabilities;

        if (mortgage.monthlyPayment === undefined) {
            mortgage.monthlyPayment = 0;
        }

        mortgage.totalSumLiabilitiesMonthly = mortgage.monthlyPayment + mortgage.totalSumOtherLiabilitiesMonthly;

        for (const amortization of mortgage.amortizations) {
            mortgage.totalSumLiabilitiesMonthly += amortization.monthlyAmount;
        }
    }

    validateMortgage(mortgage: Mortgage) {
        if (mortgage === undefined) {
            throw new Error("mortgage must be given");
        }
    }

    calcTotalLiabilities(mortgage: Mortgage) {
        mortgage.totalSumOtherLiabilities = 0;
        mortgage.totalSumOtherLiabilitiesMonthly = 0;

        if (mortgage.otherLiabilities) {
            for (const otherLiability of mortgage.otherLiabilities) {
                // total liabilities
                mortgage.totalSumOtherLiabilities += otherLiability.amount;

                // monthly liabilities
                mortgage.totalSumOtherLiabilitiesMonthly +=
                    (otherLiability.amount * otherLiability.interestRate) / 100 / 12;

                for (const amortization of otherLiability.amortizations) {
                    mortgage.totalSumOtherLiabilitiesMonthly += amortization.monthlyAmount;
                }
            }
        }

        mortgage.totalSumOtherLiabilitiesMonthly = round(mortgage.totalSumOtherLiabilitiesMonthly);
    }

    calcMonthlyAmount(amount: number, percentage: number) {
        return (amount * percentage) / 100 / 12;
    }

    calcAmortizationQuarterly(amortization: number) {
        return amortization * 3;
    }
}
